import { ChangeEvent, useCallback, useMemo, useRef } from "react";

import { twMerge } from "tailwind-merge";

import { faTrashAlt } from "@fortawesome/free-regular-svg-icons/faTrashAlt";

import FontAwesomeIcon from "components/FontAwesomeIcon";

const FileInput = ({
  file,
  children,
  extensions,
  onChange,
  preview,
  multiple,
  max,
  containerClassName,
  disabled,
}: {
  file?: File;
  children: React.ReactNode;
  extensions?: string[];
  onChange: (_file: File | File[] | undefined) => void;
  preview?: boolean;
  multiple?: boolean;
  max?: number;
  containerClassName?: string;
  disabled?: boolean;
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      if (!e.target.files?.length) return;

      if (e.target.files && !multiple) {
        const targetFile = e.target.files[0];
        onChange(targetFile);
      } else if (e.target.files && multiple) {
        const targetFiles = Array.from(e.target.files);
        onChange(targetFiles);
      }
    },
    [onChange, multiple]
  );

  const onClick = useCallback(() => {
    if (file || disabled) {
      return;
    }

    fileInputRef.current?.click();
  }, [disabled, file]);

  const onRemove = useCallback(() => {
    onChange(undefined);
  }, [onChange]);

  const filePreview = useMemo(() => {
    if (!preview || !file) return null;

    if ([".jpg", ".jpeg", ".png"].some((ext) => file.name.endsWith(ext))) {
      var url = URL.createObjectURL(file);
      // eslint-disable-next-line @next/next/no-img-element
      return <img src={url} alt="preview" className="max-w-[300px]" />;
    }

    return <div className="max-w-[300px] truncate">{file.name}</div>;
  }, [file, preview]);

  if (preview && file) {
    return (
      <div>
        <div>{filePreview}</div>
        <div className="mt-[10px] flex justify-center">
          <button
            className={twMerge(
              "border-none",
              "bg-transparent",
              "text-intelligent-silver hover:text-bearish-red",
              "text-[13px] leading-[1]",
              "px-[1.25em] py-[0.75em]"
            )}
            onClick={onRemove}
          >
            <FontAwesomeIcon icon={faTrashAlt} className="mr-[5px]" />
            Remove
          </button>
        </div>
      </div>
    );
  }

  return (
    <div onClick={onClick} className={twMerge(containerClassName)}>
      <input
        ref={fileInputRef}
        title="file"
        type="file"
        onChange={handleFileChange}
        className="hidden"
        max={multiple ? max : undefined}
        multiple={multiple}
        {...(extensions ? { accept: extensions.join(",") } : {})}
      />
      {children}
    </div>
  );
};

export default FileInput;
